import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronDown,
  CreditCard,
  ExternalLink,
  Globe,
  RefreshCw,
  Users,
  X,
} from 'lucide-react';
import { listAccounts, type AccountRow } from '../../lib/accounts-api';
import {
  billingErrorMessage,
  cancelSubscription,
  changePlan,
  formatMoney,
  getBilling,
  listInvoices,
  startCheckout,
  verifyCheckout,
  type BillingStatus,
  type InvoiceView,
  type PlanView,
} from '../../lib/billing-api';
import { useAgentStore } from '../../stores/agent';
import { useDialogFocus } from '../../lib/dialog-focus';
import { BillingPlansSection } from './BillingPlansSection';
import { Progress, Button } from '../../components/ui';
import styles from './admin.module.css';
import ui from './Billing.module.css';
import { confirmDialog } from '../../stores/confirm';

// Billing (Razorpay Subscriptions). An account-admin manages its own account's
// plan; the platform operator can look at any account and, below, see the
// plan catalogue and exemptions. Razorpay has no hosted customer portal, so
// plan changes, cancelling and invoices all live here.

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/** Optional "Custom plan → Contact us" card (a mailto: or a sales page URL). */
const SALES_CONTACT_URL = (import.meta.env.VITE_SALES_CONTACT_URL as string | undefined)?.trim() || null;

interface RazorpayHandlerResponse {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}
interface RazorpayInstance { open(): void }
type RazorpayCtor = new (options: Record<string, unknown>) => RazorpayInstance;

let checkoutScript: Promise<RazorpayCtor> | null = null;
function loadCheckout(): Promise<RazorpayCtor> {
  checkoutScript ??= new Promise<RazorpayCtor>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = CHECKOUT_SRC;
    el.async = true;
    el.onload = () => {
      const ctor = (window as unknown as { Razorpay?: RazorpayCtor }).Razorpay;
      if (ctor) resolve(ctor); else reject(new Error('Razorpay Checkout did not load'));
    };
    el.onerror = () => {
      checkoutScript = null;
      reject(new Error('Razorpay Checkout could not be loaded (blocked or offline)'));
    };
    document.head.appendChild(el);
  });
  return checkoutScript;
}

type State = BillingStatus['entitlements']['state'];
type Period = PlanView['period'];

const STATE_LABEL: Record<State, string> = {
  unmetered: 'Not metered',
  trialing: 'Free trial',
  active: 'Active',
  'past-due': 'Payment retrying',
  grace: 'Payment failed',
  free: 'Free plan',
};
const STATE_TONE: Record<State, string> = {
  unmetered: ui.stateNeutral ?? '',
  trialing: ui.stateGood ?? '',
  active: ui.stateGood ?? '',
  'past-due': ui.stateWarn ?? '',
  grace: ui.stateBad ?? '',
  free: ui.stateNeutral ?? '',
};

/** Every feature ships in every plan; plans differ only by their limits. */
const INCLUDED_FEATURES: { group: string; items: string[] }[] = [
  { group: 'Live chat & ticketing', items: ['Live chat widget and shared inbox', 'Tickets', 'Canned replies, departments and routing', 'Video calls and screen share'] },
  { group: 'AI agent', items: ['AI replies grounded in your knowledge base', 'Website, PDF and Q&A knowledge sources', 'Human handoff and follow-ups'] },
  { group: 'Automation & channels', items: ['Flows builder and templates', 'WhatsApp, Instagram, Messenger and email channels', 'Webhooks and public API'] },
  { group: 'Insights', items: ['Analytics and CSAT surveys', 'Audit log'] },
];

const limitText = (n: number | null) => (n === null ? 'Unlimited' : n.toLocaleString());
const dateText = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** 232000 paise, INR → "2,320" (decimals only when there are some). */
function priceNumber(amount: number, currency: string): string {
  const major = amount / 100;
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : undefined, {
      minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    return String(major);
  }
}

function priceText(p: Pick<PlanView, 'amount' | 'currency' | 'period'>): string {
  if (p.amount === 0) return 'Free';
  return `${formatMoney(p.amount, p.currency)} / ${p.period === 'yearly' ? 'year' : 'month'}`;
}

const sameName = (a: PlanView, b: PlanView) => a.name.trim().toLowerCase() === b.name.trim().toLowerCase();

/** Saving of a yearly plan over twelve months of its monthly twin, in %. */
function yearlySaving(yearly: PlanView, plans: PlanView[]): number | null {
  const monthly = plans.find((m) => m.period === 'monthly' && m.amount > 0 && sameName(m, yearly));
  if (!monthly || yearly.amount <= 0) return null;
  const pct = Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100);
  return pct > 0 ? pct : null;
}

function blurb(p: PlanView): string {
  if (p.amount === 0) return 'Get started with live chat and an AI agent at no cost.';
  if (p.seatLimit === null && p.siteLimit === null) return 'No limits on agents or sites, for teams that run support at scale.';
  const seats = p.seatLimit === null ? 'unlimited agents' : `up to ${p.seatLimit} agent${p.seatLimit === 1 ? '' : 's'}`;
  return `For teams with ${seats} across ${p.siteLimit === null ? 'any number of' : p.siteLimit} site${p.siteLimit === 1 ? '' : 's'}.`;
}

function planFeatures(p: PlanView): string[] {
  const cycle = p.period === 'yearly' ? 'year' : 'month';
  return [
    `${limitText(p.seatLimit)} agent seat${p.seatLimit === 1 ? '' : 's'}`,
    `${limitText(p.siteLimit)} site${p.siteLimit === 1 ? '' : 's'}`,
    `${limitText(p.aiReplyQuota)} AI replies per ${cycle}`,
    p.aiReplyQuota === null
      ? 'No AI reply cap'
      : p.overageAmountPerReply > 0
        ? `Extra AI replies at ${formatMoney(p.overageAmountPerReply, p.currency)} each`
        : 'AI pauses at the quota, agents take over',
    p.code === 'free' || !p.retrievalAmountPerCall
      ? 'Knowledge searches included'
      : `${formatMoney(p.retrievalAmountPerCall, p.currency)} per knowledge search`,
    p.code === 'free' || !p.toolAmountPerCall
      ? 'AI actions included'
      : `${formatMoney(p.toolAmountPerCall, p.currency)} per AI action`,
    'Every Openline feature included',
  ];
}

export function AdminBilling() {
  const isPlatformAdmin = useAgentStore((s) => s.current?.role) === 'super-admin';
  const me = useAgentStore((s) => s.current);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [invoices, setInvoices] = useState<InvoiceView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState<Period>('monthly');
  const [comparing, setComparing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await getBilling(accountId);
      setStatus(s);
      setInvoices(null);
      if (s.enabled && s.subscription) {
        listInvoices(accountId).then(setInvoices).catch(() => setInvoices([]));
      }
    } catch (err) {
      setError(billingErrorMessage(err, 'Could not load billing.'));
    }
  }, [accountId]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (isPlatformAdmin) listAccounts().then(setAccounts).catch(() => { });
  }, [isPlatformAdmin]);

  async function run(action: () => Promise<string | null>) {
    setBusy(true); setError(null); setNotice(null);
    try {
      const msg = await action();
      if (msg) setNotice(msg);
      await load();
    } catch (err) {
      setError(billingErrorMessage(err, 'That did not work. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function subscribe(plan: PlanView) {
    setBusy(true); setError(null); setNotice(null);
    try {
      const [Razorpay, checkout] = await Promise.all([loadCheckout(), startCheckout(plan.id, accountId)]);
      const rzp = new Razorpay({
        key: checkout.keyId,
        subscription_id: checkout.subscriptionId,
        name: 'Openline',
        description: `${plan.name} plan`,
        prefill: me ? { name: me.name, email: me.email } : undefined,
        handler: (resp: RazorpayHandlerResponse) => {
          void run(async () => {
            await verifyCheckout(
              {
                paymentId: resp.razorpay_payment_id,
                subscriptionId: resp.razorpay_subscription_id,
                signature: resp.razorpay_signature,
              },
              accountId,
            );
            return checkout.trialEndsAt
              ? `Your ${plan.name} trial has started. The first charge is on ${dateText(checkout.trialEndsAt)}; cancel before then to pay nothing.`
              : `Subscribed to ${plan.name}.`;
          });
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (err) {
      setError(err instanceof Error ? err.message : billingErrorMessage(err, 'Could not start checkout.'));
      setBusy(false);
    }
  }

  function switchTo(plan: PlanView) {
    void run(async () => {
      const r = await changePlan(plan.id, accountId);
      return r.at === 'now'
        ? `Switched to ${plan.name}. Razorpay charges the difference on the next invoice.`
        : `Switch to ${plan.name} scheduled for the end of this billing cycle.`;
    });
  }

  async function cancel() {
    if (!status?.subscription) return;
    const unfinished = status.subscription.status === 'created';
    const trial = status.entitlements.state === 'trialing';
    const ok = await confirmDialog(
      unfinished
        ? { title: 'Discard this unfinished checkout?', confirmLabel: 'Discard checkout', cancelLabel: 'Keep it' }
        : trial
          ? {
              title: 'Cancel the free trial?',
              message: 'It ends now, the account moves to the Free plan, and nothing is charged.',
              confirmLabel: 'Cancel trial',
              cancelLabel: 'Keep trial',
            }
          : {
            title: 'Cancel the subscription?',
            message: 'It stays active until the end of the current cycle, then the account moves to the Free plan.',
            confirmLabel: 'Cancel subscription',
            cancelLabel: 'Keep subscription',
          },
    );
    if (!ok) return;
    void run(async () => {
      await cancelSubscription(true, accountId);
      return unfinished ? 'Checkout discarded.' : trial ? 'Trial cancelled. Nothing was charged.' : 'Cancellation scheduled for the end of this cycle.';
    });
  }

  const sub = status?.subscription ?? null;
  const ent = status?.entitlements;
  const subscribed = sub !== null && sub.status !== 'created';
  const paying = sub !== null && ['active', 'authenticated'].includes(sub.status);
  const plans = useMemo(() => status?.plans ?? [], [status]);
  const freePlan = plans.find((p) => p.code === 'free') ?? null;
  const currentPlan: PlanView | null = subscribed ? sub.plan : freePlan;

  // Show the toggle only when both periods are actually on sale.
  const periods = useMemo(
    () => (['monthly', 'yearly'] as const).filter((x) => plans.some((p) => p.amount > 0 && p.period === x)),
    [plans],
  );
  // Start on the period the account pays in (or the only one on sale).
  const subPeriod = sub?.plan.period;
  useEffect(() => {
    if (subPeriod && periods.includes(subPeriod)) setPeriod(subPeriod);
    else if (periods.length === 1) setPeriod(periods[0]!);
  }, [subPeriod, periods]);

  const visiblePlans = plans
    .filter((p) => p.amount === 0 || p.period === period)
    .sort((a, b) => a.amount - b.amount);
  const bestSaving = Math.max(0, ...plans.filter((p) => p.period === 'yearly').map((p) => yearlySaving(p, plans) ?? 0));
  // Recommend the next tier up from what the account has now.
  const recommendedId = visiblePlans.find((p) => p.purchasable && p.amount > (currentPlan?.amount ?? 0) && p.id !== currentPlan?.id)?.id;

  return (
    <div>
      <div className={styles.headerRow}>
        <div className={styles.headerTitles}>
          <div className={styles.kicker}>
            <CreditCard size={13} />
            <span>Workspace Scope</span>
          </div>
          <h1 className={styles.title}>Plan &amp; Billing</h1>
          <p className={styles.subtitle}>
            Your plan, what you have used this cycle, and invoices.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isPlatformAdmin && accounts.length > 1 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className={styles.label} style={{ margin: 0 }}>Account</span>
              <select
                className={styles.select}
                value={accountId ?? ''}
                onChange={(e) => setAccountId(e.target.value || undefined)}
                style={{ minHeight: 36, height: 36, padding: '4px 10px', fontSize: 13 }}
              >
                <option value="">My account</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          )}
          <Button variant="outline" size="sm" onClick={() => void load()} className="gap-2 h-9 px-3 text-xs">
            <RefreshCw size={13} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {error && <p className={styles.error} style={{ marginBottom: 16 }} role="alert">{error}</p>}
      {notice && <p className={styles.notice} style={{ marginBottom: 16 }} role="status">{notice}</p>}

      {status && !status.enabled && (
        <p className={styles.notice} style={{ marginBottom: 20 }}>
          Billing is not configured. Set RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET and RAZORPAY_WEBHOOK_SECRET in
          .env.local to turn it on. Until then no plan limits apply.
        </p>
      )}

      {status && ent && (
        <>
          {ent.state === 'grace' && (
            <div className={ui.alertBox} role="alert">
              <AlertTriangle size={16} />
              <span>
                Every charge retry failed. Paid limits stay until <strong>{dateText(ent.graceUntil)}</strong>; update the
                payment method{sub?.shortUrl ? <> via <a href={sub.shortUrl} target="_blank" rel="noreferrer">Razorpay</a></> : ''} before then.
              </span>
            </div>
          )}

          {/* Current plan */}
          <section className={ui.currentCard} aria-labelledby="current-plan-name">
            <div className={ui.currentMain}>
              <span className={ui.currentKicker}>Current plan</span>
              <div className={ui.currentNameRow}>
                <h2 id="current-plan-name" className={ui.currentName}>
                  {subscribed ? sub.plan.name : ent.state === 'unmetered' ? 'Unmetered' : (freePlan?.name ?? 'Free')}
                </h2>
                <span className={`${ui.statePill} ${STATE_TONE[ent.state]}`}>{STATE_LABEL[ent.state]}</span>
                {sub?.cancelAtCycleEnd && (
                  <span className={`${ui.statePill} ${ui.stateWarn}`}>Cancels {dateText(sub.currentEnd)}</span>
                )}
              </div>
              <div className={ui.currentMeta}>
                {subscribed && <span>{priceText(sub.plan)}</span>}
                {ent.state === 'trialing' && sub ? (
                  <span>
                    Free trial until {dateText(ent.trialEndsAt)}. The first charge of {priceText(sub.plan)} is on that
                    day; cancel before then to pay nothing.
                  </span>
                ) : subscribed && sub.currentEnd && (
                  <span>{sub.cancelAtCycleEnd ? 'Ends' : 'Renews'} {dateText(sub.currentEnd)}</span>
                )}
                {!subscribed && ent.state === 'unmetered' && <span>This account is never limited.</span>}
                {sub?.status === 'created' && <span>A checkout was started but not finished.</span>}
              </div>
            </div>
            <div className={ui.currentActions}>
              {sub?.shortUrl && (ent.state === 'grace' || ent.state === 'past-due') && (
                <a className={styles.buttonGhost} href={sub.shortUrl} target="_blank" rel="noreferrer">
                  Update payment method <ExternalLink size={13} />
                </a>
              )}
              {sub && !sub.cancelAtCycleEnd && (
                <button type="button" className={styles.buttonDanger} disabled={busy} onClick={() => void cancel()}>
                  {sub.status === 'created' ? 'Discard checkout' : ent.state === 'trialing' ? 'Cancel free trial' : 'Cancel subscription'}
                </button>
              )}
            </div>
          </section>

          {/* Usage this cycle */}
          <div className={ui.meters}>
            <UsageMeter icon={<Users size={13} />} label="Agent seats" used={status.usage.seats} limit={ent.limits?.seatLimit ?? null} />
            <UsageMeter icon={<Globe size={13} />} label="Sites" used={status.usage.sites} limit={ent.limits?.siteLimit ?? null} />
            <UsageMeter
              icon={<Bot size={13} />}
              label="AI replies"
              used={status.usage.aiReplies}
              limit={ent.limits?.aiReplyQuota ?? null}
              foot={
                `${dateText(ent.period.start)} – ${dateText(ent.period.end)}` +
                (ent.limits && ent.limits.overageAmountPerReply > 0
                  ? ` · extra replies ${formatMoney(ent.limits.overageAmountPerReply, sub?.plan.currency ?? 'INR')} each`
                  : '')
              }
            />
          </div>

          {status.aiUsage && (
            <details className={ui.aiDetails}>
              <summary>
                AI usage this cycle
                <ChevronDown size={16} aria-hidden="true" />
              </summary>
              <div className={ui.aiGrid}>
                <AiStat label="Model calls" value={status.aiUsage.modelCalls.toLocaleString()} />
                <AiStat label="Knowledge searches" value={status.aiUsage.retrievals.toLocaleString()} />
                <AiStat label="Action calls" value={status.aiUsage.toolCalls.toLocaleString()} />
                <AiStat label="Failed calls" value={status.aiUsage.failedCalls.toLocaleString()} />
                <AiStat label="Billable searches" value={status.aiUsage.billableRetrievals.toLocaleString()} />
                <AiStat label="Billable actions" value={status.aiUsage.billableToolCalls.toLocaleString()} />
                <AiStat
                  label="Search/action charges"
                  value={Object.entries(status.aiUsage.operationCharges).map(([currency, amount]) => formatMoney(amount, currency)).join(', ') || 'Included'}
                />
                <AiStat label="Input tokens" value={status.aiUsage.inputTokens.toLocaleString()} />
                <AiStat label="Output tokens" value={status.aiUsage.outputTokens.toLocaleString()} />
                <AiStat label="Est. AI provider cost (USD)" value={`$${status.aiUsage.estimatedCostUsd.toFixed(4)}`} />
                <p className={ui.aiNote}>
                  Includes open conversations, indexing and evaluation in this cycle. Subscription charges use saved
                  replies plus billable searches/actions at the plan rates. Provider cost is an estimate.
                  {(status.aiUsage.unknownUsageCalls > 0 || status.aiUsage.unpricedCalls > 0) && (
                    <span className={styles.hintError}>
                      {' '}Cost is incomplete: {status.aiUsage.unknownUsageCalls} calls without token counts;{' '}
                      {status.aiUsage.unpricedCalls} calls without a model price.
                    </span>
                  )}
                </p>
              </div>
            </details>
          )}
        </>
      )}

      {status?.enabled && ent?.state !== 'unmetered' && visiblePlans.length > 0 && (
        <section aria-labelledby="plans-title">
          <div className={ui.plansHead}>
            <h2 id="plans-title" className={ui.plansTitle}>Choose the plan that fits your team</h2>
            <p className={ui.plansSubtitle}>
              Every plan includes live chat, the AI agent, Flows and every channel. Plans differ by seats, sites and
              AI replies.
            </p>
          </div>

          {periods.length > 1 && (
            <div className={ui.periodToggle}>
              <div className={ui.periodGroup} role="radiogroup" aria-label="Billing period">
                {periods.map((p) => (
                  <button
                    key={p}
                    type="button"
                    role="radio"
                    aria-checked={period === p}
                    className={ui.periodOption}
                    onClick={() => setPeriod(p)}
                  >
                    {p === 'monthly' ? 'Billed monthly' : 'Billed annually'}
                    {p === 'yearly' && bestSaving > 0 && <span className={ui.saveTag}>Save up to {bestSaving}%</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={ui.planGrid}>
            {visiblePlans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                plans={plans}
                isCurrent={currentPlan?.id === p.id}
                recommended={p.id === recommendedId}
                busy={busy}
                action={planAction(p)}
              />
            ))}
            {SALES_CONTACT_URL && <CustomPlanCard href={SALES_CONTACT_URL} />}
          </div>

          <div className={ui.compareLink}>
            <button type="button" className={ui.linkBtn} onClick={() => setComparing(true)}>
              Compare plans and features
            </button>
          </div>
          <p className={ui.footnote}>
            Upgrades apply immediately; downgrades at the end of the cycle. Razorpay allows plan changes only for card
            subscriptions. For UPI Autopay or e-mandate, cancel and subscribe to the new plan.
          </p>
        </section>
      )}

      {invoices && invoices.length > 0 && (
        <section aria-labelledby="invoices-title">
          <h3 id="invoices-title" className={styles.label} style={{ marginBottom: 12 }}>Invoices</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Issued</th><th>Period</th><th>Amount</th><th>Status</th><th style={{ textAlign: 'right' }}>Invoice</th></tr></thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id}>
                    <td>{dateText(i.issuedAt)}</td>
                    <td>{dateText(i.periodStart)} – {dateText(i.periodEnd)}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(i.amount, i.currency)}</td>
                    <td>
                      <span className={`${ui.statePill} ${i.status === 'paid' ? ui.stateGood : i.status === 'issued' ? ui.stateWarn : ui.stateNeutral}`}>
                        {i.status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {i.url && (
                        <a href={i.url} target="_blank" rel="noreferrer" className={ui.linkBtn}>
                          View <ExternalLink size={12} style={{ verticalAlign: '-1px' }} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {isPlatformAdmin && <BillingPlansSection onChange={() => void load()} />}

      {comparing && (
        <ComparePlans
          plans={visiblePlans}
          period={period}
          currentId={currentPlan?.id ?? null}
          withCustom={!!SALES_CONTACT_URL}
          onClose={() => setComparing(false)}
        />
      )}
    </div>
  );

  /** What a plan card's button does for this account, if anything. */
  function planAction(p: PlanView): PlanAction {
    if (currentPlan?.id === p.id) return { kind: 'current' };
    if (paying && sub) {
      // Moving to Free is a cancellation at the end of the cycle.
      if (p.code === 'free') return sub.cancelAtCycleEnd ? { kind: 'none' } : { kind: 'run', label: 'Downgrade', onClick: () => void cancel() };
      if (!p.purchasable) return { kind: 'none' };
      if (p.period !== sub.plan.period) {
        return { kind: 'disabled', label: p.amount > sub.plan.amount ? 'Upgrade' : 'Switch', hint: 'Cancel first to change the billing period.' };
      }
      return { kind: 'run', label: p.amount > sub.plan.amount ? 'Upgrade' : 'Downgrade', onClick: () => switchTo(p) };
    }
    if (subscribed || !p.purchasable) return { kind: 'none' };
    return { kind: 'run', label: p.trialDays > 0 ? 'Start free trial' : 'Subscribe', primary: true, onClick: () => void subscribe(p) };
  }
}

type PlanAction =
  | { kind: 'current' }
  | { kind: 'none' }
  | { kind: 'disabled'; label: string; hint: string }
  | { kind: 'run'; label: string; onClick: () => void; primary?: boolean };

function UsageMeter({ icon, label, used, limit, foot }: {
  icon: ReactNode; label: string; used: number; limit: number | null; foot?: string;
}) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  const tone = limit && used >= limit ? ui.meterFull : pct >= 80 ? ui.meterNear : '';
  return (
    <div className={`${ui.meter} ${tone}`}>
      <div className={ui.meterHead}>
        <span>{icon}{label}</span>
        <span>{limit ? `${Math.round(pct)}%` : 'Unlimited'}</span>
      </div>
      <div className={ui.meterValue}>
        {used.toLocaleString()} <small>/ {limit === null ? '∞' : limit.toLocaleString()}</small>
      </div>
      {limit !== null && <Progress value={pct} aria-label={`${label}: ${used} of ${limit}`} />}
      {foot && <span className={ui.meterFoot}>{foot}</span>}
    </div>
  );
}

function AiStat({ label, value }: { label: string; value: string }) {
  return (
    <div className={ui.aiStat}>
      <span className={ui.aiStatLabel}>{label}</span>
      <span className={ui.aiStatValue}>{value}</span>
    </div>
  );
}

function PlanCard({ plan, plans, isCurrent, recommended, busy, action }: {
  plan: PlanView; plans: PlanView[]; isCurrent: boolean; recommended: boolean; busy: boolean; action: PlanAction;
}) {
  const saving = plan.period === 'yearly' ? yearlySaving(plan, plans) : null;
  const perMonth = plan.period === 'yearly' && plan.amount > 0 ? Math.round(plan.amount / 12) : null;
  const cls = [ui.planCard, isCurrent ? ui.planCurrent : recommended ? ui.planFeatured : ''].filter(Boolean).join(' ');
  return (
    <article className={cls} aria-label={`${plan.name} plan`}>
      {isCurrent ? (
        <span className={ui.ribbon}>Current plan</span>
      ) : recommended ? (
        <span className={`${ui.ribbon} ${ui.ribbonFeatured}`}>Recommended</span>
      ) : null}
      <h3 className={ui.planName}>{plan.name}</h3>
      <p className={ui.planBlurb}>{blurb(plan)}</p>
      <div className={ui.price}>
        <span className={ui.priceValue}>{plan.amount === 0 ? '0' : priceNumber(plan.amount, plan.currency)}</span>
        <span className={ui.priceUnit}>
          <span>{plan.currency}</span>
          <span>/{plan.amount === 0 ? 'mo.' : plan.period === 'yearly' ? 'yr.' : 'mo.'}</span>
        </span>
      </div>
      <p className={ui.priceNote}>
        {perMonth !== null ? `${formatMoney(perMonth, plan.currency)} / month` : ''}
        {saving ? ` · save ${saving}%` : ''}
      </p>
      {plan.trialDays > 0 && !isCurrent && <span className={ui.trialTag}>{plan.trialDays}-day free trial</span>}
      <div className={ui.quotaLine}>
        {plan.aiReplyQuota === null ? 'Unlimited AI replies' : `${plan.aiReplyQuota.toLocaleString()} AI replies`}
      </div>

      {action.kind === 'current' ? (
        <button type="button" className={`${styles.buttonGhost} ${ui.planCta}`} disabled>Your current plan</button>
      ) : action.kind === 'run' ? (
        <button
          type="button"
          className={`${action.primary || action.label === 'Upgrade' ? styles.button : styles.buttonGhost} ${ui.planCta}`}
          disabled={busy}
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : action.kind === 'disabled' ? (
        <>
          <button type="button" className={`${styles.buttonGhost} ${ui.planCta}`} disabled title={action.hint}>
            {action.label}
          </button>
          <p className={ui.ctaHint}>{action.hint}</p>
        </>
      ) : null}

      <p className={ui.featuresLabel}>Key features:</p>
      <ul className={ui.features}>
        {planFeatures(plan).map((f) => (
          <li key={f}><Check size={15} aria-hidden="true" />{f}</li>
        ))}
      </ul>
    </article>
  );
}

function CustomPlanCard({ href }: { href: string }) {
  return (
    <article className={`${ui.planCard} ${ui.planCustom}`} aria-label="Custom plan">
      <h3 className={ui.planName}>Custom plan</h3>
      <p className={ui.planBlurb}>Tailored limits and pricing for large teams with complex requirements.</p>
      <div className={ui.price}><span className={ui.priceContact}>Contact for pricing</span></div>
      <p className={ui.priceNote} />
      <div className={ui.quotaLine}>Custom AI replies</div>
      <a className={`${styles.button} ${ui.planCta}`} href={href} target="_blank" rel="noreferrer">Contact us</a>
      <p className={ui.featuresLabel}>Key features:</p>
      <ul className={ui.features}>
        {['Custom seat and site limits', 'Custom AI reply quota', 'Every Openline feature included'].map((f) => (
          <li key={f}><Check size={15} aria-hidden="true" />{f}</li>
        ))}
      </ul>
    </article>
  );
}

function ComparePlans({ plans, period, currentId, withCustom, onClose }: {
  plans: PlanView[]; period: Period; currentId: string | null; withCustom: boolean; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(ref, onClose);
  const cols = plans.length + (withCustom ? 1 : 0);
  const cell = (v: string) => <td>{v}</td>;
  const limitRow = (label: string, get: (p: PlanView) => string) => (
    <tr>
      <th scope="row">{label}</th>
      {plans.map((p) => <td key={p.id}>{get(p)}</td>)}
      {withCustom && cell('Custom')}
    </tr>
  );
  return (
    <div className={styles.modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className={ui.compareModal} role="dialog" aria-modal="true" aria-labelledby="compare-title">
        <button type="button" className={ui.closeBtn} onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <div className={ui.compareHead}>
          <h2 id="compare-title" className={ui.compareTitle}>Compare plans and features</h2>
        </div>
        <div className={ui.compareBody}>
          <table className={ui.compareTable}>
            <thead>
              <tr>
                <th scope="col"><span className={styles.srOnly}>Feature</span></th>
                {plans.map((p) => (
                  <th key={p.id} scope="col">
                    <span className={ui.colName}>{p.name}{p.id === currentId ? ' (current)' : ''}</span>
                    <span className={ui.colPrice}>
                      {p.amount === 0 ? `${formatMoney(0, p.currency)}/mo.` : `${formatMoney(p.amount, p.currency)}/${period === 'yearly' ? 'yr.' : 'mo.'}`}
                    </span>
                  </th>
                ))}
                {withCustom && (
                  <th scope="col"><span className={ui.colName}>Custom plan</span><span className={ui.colPrice}>Contact for pricing</span></th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr className={ui.groupRow}><th colSpan={cols + 1}><span>Quota</span></th></tr>
              {limitRow('Agent seats', (p) => limitText(p.seatLimit))}
              {limitRow('Sites', (p) => limitText(p.siteLimit))}
              {limitRow(`AI replies per ${period === 'yearly' ? 'year' : 'month'}`, (p) => limitText(p.aiReplyQuota))}
              {limitRow('Extra AI replies', (p) =>
                p.aiReplyQuota === null ? '—' : p.overageAmountPerReply > 0 ? `${formatMoney(p.overageAmountPerReply, p.currency)} each` : 'Paused at quota')}
              {limitRow('Per knowledge search', (p) =>
                p.code === 'free' || !p.retrievalAmountPerCall ? 'Included' : formatMoney(p.retrievalAmountPerCall, p.currency))}
              {limitRow('Per AI action', (p) =>
                p.code === 'free' || !p.toolAmountPerCall ? 'Included' : formatMoney(p.toolAmountPerCall, p.currency))}
              {INCLUDED_FEATURES.map((g) => (
                <Fragment key={g.group}>
                  <tr className={ui.groupRow}><th colSpan={cols + 1}><span>{g.group}</span></th></tr>
                  {g.items.map((item) => (
                    <tr key={item}>
                      <th scope="row">{item}</th>
                      {Array.from({ length: cols }, (_, i) => (
                        <td key={i} className={ui.yes}><Check size={15} aria-label="Included" /></td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          <p className={ui.footnote} style={{ marginTop: 16, marginBottom: 0 }}>
            Every feature is in every plan; only the limits change.
          </p>
        </div>
      </div>
    </div>
  );
}
